const { formatMoney, getElement, getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  brandTakeoverText: 'data-product-brand-takeover',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceFrom: '[data-product-price-from]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
  zakekeProduct: '[data-zakeke-product="true"]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  setPrice () {
    unhidePriceEls()

    const priceElements = getElements(selectors.price)
    priceElements.forEach(el => (el.innerHTML = formatMoney(this.data.price)))

    const priceCompareElements = getElements(selectors.priceCompare)
    priceCompareElements.forEach(
      el => (el.innerHTML = formatMoney(this.data.compare_at_price)),
    )

    hideFromLabel()

    if (!this.data.compare_at_price) {
      hidePriceCompareElements()
    }
  }

  setAvailability () {
    getElements(selectors.addToCart).forEach(
      el => (el.disabled = !this.data.available),
    )

    getElements(selectors.addToCartText).forEach(el => {
      if (getElement(selectors.zakekeProduct) && this.data.available) {
        el.textContent = window.theme.strings.product.personalise
      } else {
        if (el.hasAttribute(selectors.brandTakeoverText)) {
          el.textContent = this.data.available
            ? window.theme.strings.brandTakeover
            : window.theme.strings.soldOut
        } else {
          el.textContent = this.data.available
            ? window.theme.strings.addToCart
            : window.theme.strings.soldOut
        }
      }
    })

    window.BAO.dispatchEvent(window.BAO.EVENTS.VARIANT.CHANGE, {
      available: this.data.available,
      variant: this.data,
    })
  }

  updateAddToCartBtn () {
    const addToCartButton = getElement(selectors.addToCart)

    if (addToCartButton && this.data) {
      addToCartButton.setAttribute('data-product-add-to-cart', '')
      addToCartButton.removeAttribute('data-drawers-trigger')
    } else {
      addToCartButton.removeAttribute('data-product-add-to-cart', '')
      addToCartButton.setAttribute('data-drawers-trigger')
    }
  }

  updateInput () {
    const inputs = getElements(selectors.form)

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange () {
    const variant = this.getVariant()

    variant.setAvailability()
    variant.setPrice()
    variant.updateInput()
    variant.updateAddToCartBtn()

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOption () {
    this.throwError(`selectedOption needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.filter(obj => {
      return (
        obj.title === this.els['option-choice'].element.selectedOptions[0].value
      )
    })[0]
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantSelect extends VariantOptions {
  get selectedOption () {
    return this.els['option-choice'].element.value || null
  }
}

if (!customElements.get('variant-select')) {
  customElements.define('variant-select', VariantSelect)
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
}

function hidePriceCompareElements () {
  hideElements(getElements(selectors.priceCompare))
}

function hideFromLabel () {
  hideElements(getElements(selectors.priceFrom))
}
